import { useEffect, useRef, useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Custom hook for WebSocket connection with automatic reconnection
 * @param {string} jobId - The job ID to connect to
 * @param {function} onMessage - Callback function for received messages
 * @param {object} options - Configuration options
 */
export const useWebSocket = (jobId, onMessage, options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = options.maxReconnectAttempts || 3; // Reduced from 5
  const reconnectInterval = options.reconnectInterval || 5000; // Increased from 3000
  const connectionTimeoutRef = useRef(null);
  const isManualDisconnectRef = useRef(false);
  
  const connect = useCallback(() => {
    if (!jobId || isManualDisconnectRef.current) {
      if (!jobId) {
        console.log(`🔌 No job ID provided, skipping WebSocket connection`);
      }
      return;
    }
    
    // Prevent multiple simultaneous connection attempts
    if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
      console.log(`🔌 WebSocket already connecting/connected for job ${jobId}, skipping`);
      return;
    }
    
    try {
      // Convert HTTP URL to WebSocket URL
      const wsUrl = API_BASE.replace('http://', 'ws://').replace('https://', 'wss://') + `/ws/job/${jobId}`;
      
      console.log(`🔌 Connecting to WebSocket: ${wsUrl} (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
      setConnectionStatus('connecting');
      setError(null);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      // Connection timeout (10 seconds)
      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.log(`⏰ WebSocket connection timeout for job ${jobId}`);
          ws.close();
          setConnectionStatus('timeout');
          setError(new Error('Connection timeout'));
          
          // Attempt reconnection
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current++;
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, reconnectInterval);
          } else {
            console.log(`❌ Max reconnection attempts reached for job ${jobId}`);
            setConnectionStatus('failed');
          }
        }
      }, 10000);
      
      ws.onopen = () => {
        console.log(`✅ WebSocket connected for job ${jobId}`);
        clearTimeout(connectionTimeoutRef.current);
        setIsConnected(true);
        setConnectionStatus('connected');
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset on successful connection
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`📨 WebSocket message received for job ${jobId}:`, data);
          
          setLastMessage(data);
          
          if (onMessage) {
            onMessage(data);
          }
        } catch (err) {
          console.warn('Failed to parse WebSocket message:', err);
        }
      };
      
      ws.onclose = (event) => {
        console.log(`🔌 WebSocket disconnected for job ${jobId}. Code: ${event.code}, Reason: ${event.reason}`);
        clearTimeout(connectionTimeoutRef.current);
        setIsConnected(false);
        
        // Don't reconnect if manually disconnected
        if (isManualDisconnectRef.current) {
          setConnectionStatus('disconnected');
          return;
        }
        
        // Handle different close codes
        if (event.code === 1006) {
          console.log(`❌ Abnormal closure (1006) for job ${jobId} - server may be down or overloaded`);
          setConnectionStatus('server_error');
          setError(new Error('Server connection lost'));
        } else if (event.code === 1000) {
          console.log(`✅ Normal closure for job ${jobId}`);
          setConnectionStatus('disconnected');
          return; // Don't reconnect on normal closure
        } else if (event.code === 1011) {
          console.log(`❌ Server error (1011) for job ${jobId}`);
          setConnectionStatus('server_error');
          setError(new Error('Server error'));
        } else {
          console.log(`⚠️ Unexpected closure (${event.code}) for job ${jobId}`);
          setConnectionStatus('unexpected_close');
          setError(new Error(`Unexpected closure: ${event.code}`));
        }
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const backoffDelay = Math.min(reconnectInterval * Math.pow(2, reconnectAttemptsRef.current), 30000); // Max 30 seconds
          console.log(`🔄 Attempting to reconnect WebSocket for job ${jobId} in ${backoffDelay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          reconnectAttemptsRef.current++;
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!isManualDisconnectRef.current) {
              connect();
            }
          }, backoffDelay);
        } else {
          console.log(`❌ Max reconnection attempts reached for job ${jobId}, giving up`);
          setConnectionStatus('failed');
          setError(new Error('Failed to reconnect after multiple attempts'));
        }
      };
      
      ws.onerror = (error) => {
        console.error(`❌ WebSocket error for job ${jobId}:`, error);
        setError(error);
        setConnectionStatus('error');
      };
      
    } catch (err) {
      console.error(`❌ Failed to create WebSocket connection for job ${jobId}:`, err);
      setError(err);
      setConnectionStatus('error');
    }
  }, [jobId, onMessage, maxReconnectAttempts, reconnectInterval]);
  
  const disconnect = useCallback(() => {
    if (!jobId) {
      console.log(`🔌 No job ID to disconnect`);
      return;
    }
    console.log(`🔌 Manually disconnecting WebSocket for job ${jobId}`);
    isManualDisconnectRef.current = true;
    
    // Clear all timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
    reconnectAttemptsRef.current = 0;
  }, [jobId]);
  
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);
  
  // Connect when jobId changes
  useEffect(() => {
    if (jobId) {
      connect();
    }
    
    // Cleanup on unmount or jobId change
    return () => {
      disconnect();
    };
  }, [jobId, connect, disconnect]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);
  
  return {
    isConnected,
    connectionStatus,
    lastMessage,
    error,
    sendMessage,
    connect,
    disconnect
  };
};

export default useWebSocket;

